import React, { useState } from 'react';
import { object, number, string, func, array, bool } from 'prop-types';
import onWindowResize from 'library/hoc/on-window-resize';
import { Header4, Header5 } from 'library/components/text-elements/headings';
import KeyCodes from 'library/helpers/key-codes';
import useWithA11y from 'library/hooks/use-with-a11y';
import withScreenSizes from 'util/hoc/with-screen-sizes';
import * as StyledFilterModal from './styled-filter-modal';
import Logo from 'apps/journey/components/header/components/journey-logo';

import {
  StyledFilterPanelContainer,
  StyledFilterPanelMobileContainer,
} from 'apps/journey/pages/career-exploration/components/filters/styled-filter-panel';
import FilterControls from 'apps/journey/pages/career-exploration/components/filters/filter-controls';

import ClearAllButton from 'apps/journey/pages/career-exploration/components/clear-all-button/clear-all-button';

const FilterPanel = ({
  title,
  clearFilters,
  handleSearch,
  handleSort,
  sortOptions,
  searchValue = '',
  sortValues = [],
  children,
  filteredResultsCount,
  filtersActive,
  windowWidth,
  screenSizes,
}) => {
  const [modalActive, setModalActive] = useState(false);
  const { isTabbing } = useWithA11y();

  const handleKeyPress = (e, actionCallback) => {
    e.preventDefault();
    if (KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      actionCallback();
    }
  };

  const closeModal = () => {
    setModalActive(false);
  };

  return windowWidth > screenSizes.tablet ? (
    <StyledFilterPanelContainer>
      {children}
      <ClearAllButton
        aria-label="Clear all"
        label="Clear all"
        clearFilters={clearFilters}
      />
    </StyledFilterPanelContainer>
  ) : (
    <StyledFilterPanelMobileContainer>
      <FilterControls
        sortOptions={sortOptions}
        searchValue={searchValue}
        sortValues={sortValues}
        handleSearch={handleSearch}
        handleSort={handleSort}
        filterCallback={() => setModalActive(!modalActive)}
        sortControl
        filterControl
        searchControl
      />
      <StyledFilterModal.StyledModal
        isTabbing={isTabbing}
        tabIndex={0}
        active={modalActive}
      >
        <StyledFilterModal.StickyHeader>
          <StyledFilterModal.HeaderContent>
            <Logo width="36px" height="36px" noBorder />
            <Header5>{title}</Header5>
            <StyledFilterModal.CloseIcon
              isTabbing={isTabbing}
              tabIndex={0}
              aria-label="Close Modal"
              onKeyPress={(e) => handleKeyPress(e, closeModal)}
              onClick={closeModal}
            />
          </StyledFilterModal.HeaderContent>
        </StyledFilterModal.StickyHeader>
        <StyledFilterModal.Body>
          <Header4>Filters</Header4>
          <StyledFilterModal.Content role="document">
            {children}
          </StyledFilterModal.Content>
          <StyledFilterModal.ButtonRow>
            <StyledFilterModal.ClearButton
              isTabbing={isTabbing}
              tabIndex={0}
              aria-label="Clear all filters"
              onKeyPress={(e) => handleKeyPress(e, clearFilters)}
              onClick={clearFilters}
              filtersActive={filtersActive}
            >
              Clear All
            </StyledFilterModal.ClearButton>
            <StyledFilterModal.ShowButton
              isTabbing={isTabbing}
              tabIndex={0}
              aria-label="Show all results"
              onKeyPress={(e) => handleKeyPress(e, closeModal)}
              onClick={closeModal}
            >
              Show {filteredResultsCount} Results
            </StyledFilterModal.ShowButton>
          </StyledFilterModal.ButtonRow>
        </StyledFilterModal.Body>
      </StyledFilterModal.StyledModal>
    </StyledFilterPanelMobileContainer>
  );
};

FilterPanel.propTypes = {
  title: string,
  searchValue: string,
  sortValues: array,
  clearFilters: func,
  handleSearch: func,
  handleSort: func,
  sortOptions: array,
  children: object,
  filteredResultsCount: number,
  filtersActive: bool,
  windowWidth: number,
  screenSizes: object,
};
export default React.memo(withScreenSizes(onWindowResize(FilterPanel)));
